'use client';

import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { TotalHits } from '@opensearch-project/opensearch/api/_types/_core.search';
import { Sort } from '@opensearch-project/opensearch/api/_types/_common';

import { InitialProductQueryFilters } from '@/constants';
import { EnumCustomerType, TreezStore } from '@/types';

import { OpenSearchContext } from './OpenSearchContext';
import { useSearch } from '../hooks';
import {
  buildFacetQueries,
  buildPriceFilterFromQueryRecord,
  buildQueryFilterFromSearchParams,
  buildSearchQueryFilter,
  buildSmartFacetAggregations,
} from '../utils';
import { DefaultSortingFilter } from '../constants';

const DEFAULT_PAGE_SIZE = 20;

interface ProviderProps extends PropsWithChildren {
  initialQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialFixedQueryFiltersByFacets?: Record<string, QueryContainer>;
  initialSortFilter?: Sort;
  initialSearchFilter?: string;
  id?: string;
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;
  includePromoItems?: boolean;
}

const OpenSearchProvider: FC<ProviderProps> = ({
  children,
  initialQueryFiltersByFacets = {},
  initialFixedQueryFiltersByFacets = {},
  initialSortFilter,
  initialSearchFilter = '',
  id,
  selectedCustomerType,
  selectedStore,
  includePromoItems = false,
}) => {
  const [pageSize, setPageSize] = useState<number | undefined>(undefined);
  const [size, setSize] = useState<number | undefined>(pageSize);
  const [sortFilter, setSortFilter] = useState<Sort | undefined>(
    initialSortFilter,
  );
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [searchableAtributes, setSearchableAttributes] = useState<
    string[] | undefined
  >(undefined);
  const [queryFiltersByFacets, setQueryFiltersByFacets] = useState(
    initialQueryFiltersByFacets,
  );
  const [fixedQueryFiltersByFacets, setFixedQueryFiltersByFacets] = useState(
    initialFixedQueryFiltersByFacets,
  );

  useEffect(() => {
    setQueryFiltersByFacets(initialQueryFiltersByFacets);
  }, [initialQueryFiltersByFacets]);

  useEffect(() => {
    setFixedQueryFiltersByFacets(initialFixedQueryFiltersByFacets);
  }, [initialFixedQueryFiltersByFacets]);

  useEffect(() => {
    setSortFilter(initialSortFilter);
  }, [initialSortFilter]);

  useEffect(() => {
    setSearchFilter(initialSearchFilter);
  }, [initialSearchFilter]);

  const priceFilter = buildPriceFilterFromQueryRecord(
    queryFiltersByFacets,
    includePromoItems,
  );

  const queryFiltersRecord = useMemo(
    () => ({
      ...buildSearchQueryFilter(searchFilter),
      ...buildQueryFilterFromSearchParams(InitialProductQueryFilters),
      ...queryFiltersByFacets,
      ...fixedQueryFiltersByFacets,
      ...priceFilter,
    }),
    [queryFiltersByFacets, fixedQueryFiltersByFacets, searchFilter],
  );

  const searchBody: Search_RequestBody = {
    from: 0,
    size: size || DEFAULT_PAGE_SIZE,
    query: buildFacetQueries(queryFiltersRecord),
    aggs: buildSmartFacetAggregations(queryFiltersRecord),
    ...(sortFilter
      ? { sort: sortFilter }
      : !searchFilter
      ? { sort: DefaultSortingFilter }
      : {}),
  };

  const { data, isLoading, status, refetch, isFetching } = useSearch({
    searchBody,
    id,
    selectedCustomerType,
    selectedStore,
  });

  const hits = data?.hits?.hits ?? [];
  const totalHits = (data?.hits?.total as TotalHits)?.value ?? 0;
  const aggregations = data?.aggregations ?? {};

  return (
    <OpenSearchContext
      value={{
        searchBody,
        hits,
        totalHits,
        aggregations,
        queryFiltersByFacets,
        updateQueryFiltersByFacets: setQueryFiltersByFacets,
        fixedQueryFiltersByFacets,
        updateFixedQueryFiltersByFacets: setFixedQueryFiltersByFacets,
        isLoading,
        isFetching,
        pageSize: pageSize || DEFAULT_PAGE_SIZE,
        updatePageSize: setPageSize,
        updateSize: setSize,
        searchableAtributes,
        updateSearchableAttributes: setSearchableAttributes,
        status,
        refetch,
        sortFilter,
        updateSortFilter: setSortFilter,
        searchFilter,
        updateSearchFilter: setSearchFilter,
      }}
    >
      {children}
    </OpenSearchContext>
  );
};

export { OpenSearchProvider };
